import React, { useState } from 'react';
import { 
  FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaHeadset, FaCheckCircle, FaChevronDown, FaPaperPlane 
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const Support = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "How can I contact Lotoswealth customer support?",
      a: "You can reach our support team via phone at +91 889 786 6074, via email at info@lotoswealth.com, or by visiting our office in Hyderabad, Telangana."
    },
    {
      q: "What are your customer service working hours?",
      a: "Our support and advisory desk operates Monday through Saturday, from 9:00 AM to 7:00 PM IST."
    },
    {
      q: "How long does it take for your team to respond to email inquiries?",
      a: "We strive to respond to all client inquiries, investment queries, and loan support requests within 24 business hours."
    },
    {
      q: "Can I schedule an in-person financial consultation?",
      a: "Yes! You can book an appointment with our wealth management experts by calling us directly or submitting an inquiry through our Contact Us page."
    }
  ];

  return (
    <main className="support-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Customer Support | Help & Contact Information | Lotoswealth</title>

        <meta
          name="description"
          content="Get in touch with Lotoswealth customer support. Reach out for assistance with wealth management, portfolio planning, loans, and financial advisory services."
        />

        <meta
          name="keywords"
          content="Lotoswealth Support, Customer Service, Financial Advisory Help, Contact Lotoswealth, Wealth Management Support, Hyderabad Financial Services"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link
          rel="canonical"
          href="https://www.lotoswealth.com/support"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta
          property="og:url"
          content="https://www.lotoswealth.com/support"
        />
        <meta
          property="og:title"
          content="Customer Support | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Connect with our expert support team for prompt assistance regarding investments, loans, and wealth advisory."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Customer Support | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Need assistance with your financial portfolio or loan application? Contact Lotoswealth support today."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>

      {/* =========================================
          1. HERO SECTION
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '60vh', minHeight: '450px', overflow: 'hidden', backgroundColor: '#0a192f' }}>
        
        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 opacity-25">
          <img 
            src="/images/lotoswealth-bg.jpg" 
            alt="Support Background" 
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              Customer Support Center
          </h1>
          <p className="hero-description animate-slide-up delay-2 text-white mx-auto" style={{ maxWidth: '800px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            We are here to help you navigate your financial journey. Reach out to our dedicated support team for prompt assistance and expert guidance.
          </p>
        </div>
      </div>

      {/* =========================================
          2. CONTACT CARDS SECTION
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">How Can We Assist You Today?</h2>
            <p className="text-muted">Choose your preferred channel to connect with our support desk.</p>
          </div>

          <div className="row g-4">
            
            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100 text-center border-top border-teal border-4">
                <FaPhoneAlt size={36} className="text-teal mb-3" />
                <h4 className="fw-bold mb-2">Call Us Directly</h4>
                <p className="text-muted small mb-3">Speak with our financial advisors during working hours.</p>
                <a href="tel:+918897866074" className="text-decoration-none fw-bold text-dark fs-5">+91 889 786 6074</a>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100 text-center border-top border-teal border-4">
                <FaEnvelope size={36} className="text-teal mb-3" />
                <h4 className="fw-bold mb-2">Email Support</h4>
                <p className="text-muted small mb-3">Send us your inquiries and receive a response within 24 hours.</p>
                <a href="mailto:info@lotoswealth.com" className="text-decoration-none fw-bold text-dark fs-5">info@lotoswealth.com</a>
              </div>
            </div>

            <div className="col-lg-4 col-md-12 animate-slide-up delay-3">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100 text-center border-top border-teal border-4">
                <FaMapMarkerAlt size={36} className="text-teal mb-3" />
                <h4 className="fw-bold mb-2">Visit Our Office</h4>
                <p className="text-muted small mb-3">Drop by our headquarters for an in-person consultation.</p>
                <span className="fw-bold text-dark d-block">Hyderabad, Telangana, India</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          3. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5 bg-white">
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Quick answers to common support and service inquiries</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-light rounded-3 shadow-sm border overflow-hidden"
              >
                <button 
                  className="w-100 text-start p-3 border-0 bg-light d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown 
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`} 
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-white lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          4. FINAL CTA SECTION
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Need Immediate Assistance?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Our team is ready to provide expert guidance tailored to your financial goals. Get in touch with us today.
          </p>
          <a href="/contact-us" className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3 text-decoration-none d-inline-block">
            Contact Us Today
          </a>
        </div>
      </section>

    </main>
  );
};

export default Support;